import { motion } from "framer-motion";
import { Link } from "@tanstack/react-router";
import { ArrowRight, Phone, Mail, MessageCircle } from "lucide-react";

// ─── Palette ─────────────────────────────────────────────────────────────────
const BK  = "#090909";
const WH  = "#FFFFFF";
const OW  = "#F5F3EE";
const GD  = "#C9A84C";
const GDL = "#E2CC7A";
const GDD = "#8C6E1E";
const GR  = "#8A8880";
const GRL = "#D0CEC8";

const FD = "'Barlow Condensed', Impact, sans-serif";
const FB = "'Barlow', -apple-system, BlinkMacSystemFont, 'Inter', system-ui, sans-serif";
const FI = "'Playfair Display', Georgia, serif";

const FEATURES = [
  "School Payments", "Digital Identity", "Transport Tracking", "Health Records",
  "Parent Messaging", "Marketplace", "Insights & Reports", "Bus GPS", "Attendance",
  "Payroll", "Procurement", "Student Portal", "Canteen Payments", "Document Vault",
];

const STATS = [
  { v: "126+", l: "Schools" },
  { v: "19K+", l: "Parents" },
  { v: "2.8B",  l: "UGX Processed" },
  { v: "96",    l: "Buses Live" },
];

const ease = [0.16, 1, 0.3, 1] as const;

// ─── Background: very faint warm radial + minimal concentric rings ────────────
function BackgroundGraphic() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <svg viewBox="0 0 1400 960" className="absolute inset-0 h-full w-full"
        preserveAspectRatio="xMidYMid slice" aria-hidden>
        <defs>
          <radialGradient id="bgBase" cx="50%" cy="38%" r="68%">
            <stop offset="0%"   stopColor={WH} />
            <stop offset="70%"  stopColor="#F3F0E8" />
            <stop offset="100%" stopColor="#EAE6DC" />
          </radialGradient>
        </defs>
        <rect width="1400" height="960" fill="url(#bgBase)" />
        {/* Two very faint rings only — invisible on small screens */}
        {[280, 480].map((r) => (
          <circle key={r} cx="700" cy="360" r={r} fill="none"
            stroke={GD} strokeWidth="0.6" strokeOpacity="0.04" />
        ))}
      </svg>
    </div>
  );
}

// ─── School supply line-art: inspired by the reference image, ultra-subtle gold ─
// Each element is a standalone SVG path, rendered at very low opacity.
// On mobile they are hidden; on md+ they appear at reduced size in the margins.

function PencilIcon() {
  return (
    <div className="pointer-events-none absolute hidden md:block"
      style={{ left: 32, top: "18%", width: 36, height: 120, opacity: 0.13 }}
      aria-hidden>
      <svg viewBox="0 0 36 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-full w-full">
        {/* Pencil body */}
        <rect x="13" y="8" width="10" height="90" rx="1" stroke={GD} strokeWidth="1.4" fill="none" />
        {/* Pencil tip triangle */}
        <path d="M13 98 L18 112 L23 98 Z" stroke={GD} strokeWidth="1.2" fill="none" strokeLinejoin="round" />
        {/* Pencil eraser cap */}
        <rect x="13" y="4" width="10" height="6" rx="1" stroke={GD} strokeWidth="1.2" fill="none" />
        {/* Eraser band */}
        <line x1="13" y1="10" x2="23" y2="10" stroke={GD} strokeWidth="1" />
        {/* Dark side stripe */}
        <rect x="19" y="8" width="4" height="90" rx="0.5" stroke={GD} strokeWidth="0.6" fill={`${GD}18`} />
      </svg>
    </div>
  );
}

function SharpenerIcon() {
  return (
    <div className="pointer-events-none absolute hidden md:block"
      style={{ right: "8%", top: "8%", width: 80, height: 72, opacity: 0.11, transform: "rotate(-14deg)" }}
      aria-hidden>
      <svg viewBox="0 0 80 72" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-full w-full">
        {/* Outer body */}
        <rect x="4" y="4" width="52" height="44" rx="4" stroke={GD} strokeWidth="1.4" fill="none" />
        {/* Hole cutout */}
        <path d="M8 32 Q8 48 24 48 Q40 48 40 32" stroke={GD} strokeWidth="1.2" fill="none" />
        {/* Inner window */}
        <rect x="14" y="10" width="22" height="24" rx="3" stroke={GD} strokeWidth="1" fill="none" />
        {/* Screw circle */}
        <circle cx="28" cy="22" r="4.5" stroke={GD} strokeWidth="1" fill="none" />
        {/* Dark side shadow panel */}
        <rect x="46" y="4" width="12" height="44" rx="0 4 4 0" stroke={GD} strokeWidth="0.6" fill={`${GD}14`} />
      </svg>
    </div>
  );
}

function OpenBookIcon() {
  return (
    <div className="pointer-events-none absolute hidden lg:block"
      style={{ right: "4%", bottom: "22%", width: 88, height: 72, opacity: 0.10, transform: "rotate(8deg)" }}
      aria-hidden>
      <svg viewBox="0 0 88 72" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-full w-full">
        {/* Left page */}
        <path d="M4 8 L42 2 L42 64 L4 68 Z" stroke={GD} strokeWidth="1.3" fill="none" strokeLinejoin="round" />
        {/* Right page */}
        <path d="M84 8 L46 2 L46 64 L84 68 Z" stroke={GD} strokeWidth="1.3" fill="none" strokeLinejoin="round" />
        {/* Spine */}
        <line x1="44" y1="2" x2="44" y2="64" stroke={GD} strokeWidth="1" />
        {/* Page lines left */}
        {[18, 28, 38, 48].map(y => (
          <line key={y} x1="10" y1={y} x2="38" y2={y - 2} stroke={GD} strokeWidth="0.7" strokeOpacity="0.7" />
        ))}
        {/* Page lines right */}
        {[18, 28, 38, 48].map(y => (
          <line key={y} x1="50" y1={y - 2} x2="78" y2={y} stroke={GD} strokeWidth="0.7" strokeOpacity="0.7" />
        ))}
        {/* Dark spine shadow */}
        <path d="M42 2 L46 2 L46 64 L42 64 Z" fill={`${GD}18`} />
      </svg>
    </div>
  );
}

function GridPaperIcon() {
  return (
    <div className="pointer-events-none absolute hidden lg:block"
      style={{ left: "5%", bottom: "18%", width: 76, height: 76, opacity: 0.09, transform: "rotate(-8deg)" }}
      aria-hidden>
      <svg viewBox="0 0 76 76" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-full w-full">
        {/* Outer border */}
        <rect x="2" y="2" width="72" height="72" rx="1" stroke={GD} strokeWidth="1.3" fill="none" />
        {/* Grid columns */}
        {[14, 26, 38, 50, 62].map(x => (
          <line key={x} x1={x} y1="2" x2={x} y2="74" stroke={GD} strokeWidth="0.7" />
        ))}
        {/* Grid rows */}
        {[14, 26, 38, 50, 62].map(y => (
          <line key={y} x1="2" y1={y} x2="74" y2={y} stroke={GD} strokeWidth="0.7" />
        ))}
      </svg>
    </div>
  );
}

function FloatingCircles() {
  return (
    <>
      {/* Large faint circle — top right */}
      <div className="pointer-events-none absolute hidden sm:block"
        style={{ right: "14%", top: "6%", width: 48, height: 48, opacity: 0.10 }} aria-hidden>
        <svg viewBox="0 0 48 48" fill="none">
          <circle cx="24" cy="24" r="20" stroke={GD} strokeWidth="1.2" fill="none" />
        </svg>
      </div>
      {/* Small dot circle — center-right */}
      <div className="pointer-events-none absolute hidden sm:block"
        style={{ right: "22%", top: "38%", width: 22, height: 22, opacity: 0.12 }} aria-hidden>
        <svg viewBox="0 0 22 22" fill="none">
          <circle cx="11" cy="11" r="8" stroke={GD} strokeWidth="1.1" fill="none" />
        </svg>
      </div>
    </>
  );
}

// ─── Thin gold rule ────────────────────────────────────────────────────────────
function GoldBar({ pos }: { pos: "top" | "bottom" }) {
  return (
    <div className={`pointer-events-none absolute ${pos}-0 left-0 right-0 z-20`}
      style={{ height: 2, background: `linear-gradient(90deg, ${GDD}, ${GD}, ${GDL}, ${GD}, ${GDD})` }} />
  );
}

// ─── Scrolling feature marquee ─────────────────────────────────────────────────
function FeatureMarquee() {
  const doubled = [...FEATURES, ...FEATURES];
  return (
    <div className="relative w-full overflow-hidden"
      style={{ borderTop: `1px solid ${BK}0C`, borderBottom: `1px solid ${BK}0C`, background: WH }}>
      <div className="pointer-events-none absolute left-0 top-0 bottom-0 z-10 w-12 sm:w-16"
        style={{ background: `linear-gradient(90deg, ${WH}, transparent)` }} />
      <div className="pointer-events-none absolute right-0 top-0 bottom-0 z-10 w-12 sm:w-16"
        style={{ background: `linear-gradient(270deg, ${WH}, transparent)` }} />
      <div className="flex items-center py-3.5"
        style={{ animation: "marqueeSlide 36s linear infinite", width: "max-content" }}>
        {doubled.map((feat, i) => (
          <div key={i} className="flex items-center shrink-0">
            <span className="text-[11px] font-bold uppercase tracking-[0.2em] px-4 sm:px-5"
              style={{ color: BK, fontFamily: FB, whiteSpace: "nowrap" }}>{feat}</span>
            <svg width="7" height="7" viewBox="0 0 8 8" className="shrink-0" aria-hidden>
              <rect x="1" y="1" width="6" height="6" fill={GD} transform="rotate(45 4 4)" />
            </svg>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
export function HeroVisual() {
  return (
    <>
      <style>{`
        @keyframes marqueeSlide {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
      `}</style>

      <section className="relative min-h-screen overflow-hidden" style={{ fontFamily: FB }}>
        <BackgroundGraphic />

        {/* School supply illustrations — subtle line art, hidden on mobile */}
        <PencilIcon />
        <SharpenerIcon />
        <OpenBookIcon />
        <GridPaperIcon />
        <FloatingCircles />

        <GoldBar pos="top" />

        {/* ── Main content ── */}
        <div className="relative z-10 mx-auto flex min-h-screen max-w-[900px] flex-col items-center px-5 pb-16 pt-20 sm:px-10 sm:pt-28">

          {/* Brand badge */}
          <motion.div initial={{ opacity: 0, y: -14 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease }}
            className="flex items-center gap-2.5 border px-4 py-2 sm:px-5 sm:py-2.5"
            style={{ borderColor: `${BK}18`, background: WH, boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}>
            <svg width="16" height="18" viewBox="0 0 18 20" fill="none" aria-hidden>
              <path d="M9 0L18 3.8V10C18 15.2 14.2 18.8 9 20 3.8 18.8 0 15.2 0 10V3.8Z" fill={BK} />
              <circle cx="9" cy="8.5" r="2.5" fill={GD} />
              <path d="M6 13.5Q9 11.5 12 13.5" stroke={GD} strokeWidth="1.4" fill="none" strokeLinecap="round" />
            </svg>
            <span style={{ fontFamily: FB, fontWeight: 700, fontSize: 12, letterSpacing: "0.16em", textTransform: "uppercase", color: BK }}>
              TriSphere
            </span>
          </motion.div>

          {/* Headline */}
          <div className="mt-6 w-full text-center">
            <motion.div initial={{ opacity: 0, y: 36 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.08, ease }}
              style={{
                fontFamily: FD, fontWeight: 900, color: BK,
                lineHeight: 0.84, letterSpacing: "-0.01em", textTransform: "uppercase",
                fontSize: "clamp(4rem, 16.5vw, 10rem)",
              }}>
              Run It All.
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 36 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.18, ease }}
              className="flex flex-wrap items-end justify-center gap-x-3"
              style={{
                fontFamily: FD, fontWeight: 800, lineHeight: 0.88,
                letterSpacing: "-0.01em", textTransform: "uppercase",
                fontSize: "clamp(2rem, 8.5vw, 5.2rem)",
              }}>
              <span style={{ color: BK }}>Pay.</span>
              <span style={{ color: BK }}>Track.</span>
              <span style={{ color: GD }}>Grow.</span>
            </motion.div>
          </div>

          {/* Subtitle */}
          <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.34, ease }}
            className="mt-5 max-w-[520px] text-center text-sm leading-relaxed sm:text-base lg:text-lg"
            style={{ fontFamily: FI, fontStyle: "italic", color: GR }}>
            One platform runs every school — collecting fees, tracking buses, managing health records,
            enrolling students, and predicting what happens next. Eight products. One ledger. Zero spreadsheets.
          </motion.p>

          {/* Gold rule */}
          <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }}
            transition={{ duration: 0.5, delay: 0.46, ease }}
            className="mt-5" style={{ height: 2, width: 48, background: GD, transformOrigin: "center" }} />

          {/* Marquee */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.56, ease }} className="mt-8 w-full">
            <FeatureMarquee />
          </motion.div>

          {/* CTAs */}
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.68, ease }}
            className="mt-8 flex flex-col items-center gap-3 sm:flex-row">
            <Link to="/auth"
              className="group inline-flex h-12 sm:h-14 items-center gap-3 px-6 sm:px-8 text-xs sm:text-sm font-bold uppercase tracking-[0.18em] sm:tracking-[0.2em] transition-opacity hover:opacity-90"
              style={{ background: BK, color: WH, fontFamily: FB }}>
              <span>LET'S</span>
              <span style={{ color: GD }}>GET STARTED</span>
              <span className="flex h-6 w-6 sm:h-7 sm:w-7 items-center justify-center transition-transform group-hover:translate-x-0.5"
                style={{ background: GD }}>
                <ArrowRight className="h-3.5 w-3.5 sm:h-4 sm:w-4" style={{ color: BK }} strokeWidth={2.5} />
              </span>
            </Link>
            <Link to="/demo"
              className="inline-flex h-12 sm:h-14 items-center px-6 sm:px-8 text-xs sm:text-sm font-bold uppercase tracking-[0.18em] sm:tracking-[0.2em] transition-opacity hover:opacity-70"
              style={{ border: `1.5px solid ${BK}22`, color: BK, fontFamily: FB }}>
              View Demo
            </Link>
          </motion.div>

          {/* Stats grid */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.8, ease }}
            className="mt-10 grid grid-cols-4 w-full" style={{ border: `1px solid ${BK}0D` }}>
            {STATS.map((s, i) => (
              <div key={s.l} className="flex flex-col items-center py-3 px-1 text-center sm:py-3.5 sm:px-2"
                style={{ borderRight: i < STATS.length - 1 ? `1px solid ${BK}0D` : "none" }}>
                <span className="font-black leading-none"
                  style={{ fontFamily: FD, fontWeight: 900, fontSize: "clamp(1.1rem, 4vw, 2rem)", color: BK, letterSpacing: "-0.02em" }}>
                  {s.v}
                </span>
                <span className="mt-1 font-bold uppercase"
                  style={{ fontFamily: FB, fontSize: 8, letterSpacing: "0.16em", color: GDD }}>
                  {s.l}
                </span>
              </div>
            ))}
          </motion.div>

          {/* Contact bar */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.94 }}
            className="mt-4 flex flex-wrap items-center justify-center gap-4 sm:gap-5 border px-4 py-3 sm:px-6 sm:py-3.5 w-full"
            style={{ borderColor: `${BK}0D`, background: WH }}>
            <a href="https://wa.me/256700000000"
              className="flex items-center gap-1.5 text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.1em] sm:tracking-[0.12em] transition-opacity hover:opacity-70"
              style={{ color: BK, fontFamily: FB }}>
              <MessageCircle className="h-3 w-3 sm:h-3.5 sm:w-3.5" style={{ color: GDD }} strokeWidth={2} />
              WhatsApp
            </a>
            <div className="h-3.5 w-px" style={{ background: GRL }} />
            <a href="tel:+256781000000"
              className="flex items-center gap-1.5 text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.1em] sm:tracking-[0.12em] transition-opacity hover:opacity-70"
              style={{ color: BK, fontFamily: FB }}>
              <Phone className="h-3 w-3 sm:h-3.5 sm:w-3.5" style={{ color: GDD }} strokeWidth={2} />
              <span className="hidden xs:inline">+256 781 000 000</span>
              <span className="xs:hidden">Call Us</span>
            </a>
            <div className="h-3.5 w-px" style={{ background: GRL }} />
            <a href="mailto:hello@trisphere.live"
              className="flex items-center gap-1.5 text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.1em] sm:tracking-[0.12em] transition-opacity hover:opacity-70"
              style={{ color: BK, fontFamily: FB }}>
              <Mail className="h-3 w-3 sm:h-3.5 sm:w-3.5" style={{ color: GDD }} strokeWidth={2} />
              <span className="hidden sm:inline">hello@trisphere.live</span>
              <span className="sm:hidden">Email</span>
            </a>
          </motion.div>

        </div>

        <GoldBar pos="bottom" />
      </section>
    </>
  );
}
